import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { vatLabel } from '../components/Invoice.js';
import { formatMoney, formatPrice } from '../components/site.js';
import {
  Button,
  Card,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SelectField,
  TextField,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate, intlLocale } from '../i18n.js';
import { useMe } from '../session.js';

type PaidPlan = 'starter' | 'business' | 'agency';
type Interval = 'month' | 'year';
type Address = Schemas['StoredAddress'];
type Method = 'transfer' | 'online';
type Plan = Schemas['Plan'];

const PAID: PaidPlan[] = ['starter', 'business', 'agency'];

/** The quote asks VIES only once typing has paused (T-63). */
function useSettled<T>(value: T, ms = 600): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

const go = (url: string) => {
  window.location.assign(url);
};

const isPlan = (v: string | null): v is PaidPlan => PAID.includes(v as PaidPlan);

/** A running plan on invoice, as `bookPayment` extends it: manual, active, ending later. */
function runsUntil(sub: Schemas['Billing']['subscription']) {
  return sub?.provider === 'manual' &&
    sub.status === 'active' &&
    sub.currentPeriodEnd &&
    Date.parse(sub.currentPeriodEnd) > Date.now()
    ? { plan: sub.planCode, end: sub.currentPeriodEnd }
    : null;
}

/** An answer of the server about the address or VAT ID: shown at the fields, reset on a change. */
const refusedAddress = (error: unknown) =>
  error instanceof ApiError && ['VALIDATION_FAILED', 'VAT_ID_INVALID'].includes(error.code);

/**
 * Plan, term, address, payment method and the summary with VAT of a plan order (checkout.html):
 * the form on the left, the order beside it with the button that sends the form.
 */
function OrderForm({
  initial,
  countries,
  plans,
  plan,
  interval,
  onChoose,
  methods,
  live,
  runsUntil,
}: {
  initial: Address;
  countries: string[];
  plans: Plan[];
  plan: PaidPlan;
  interval: Interval;
  onChoose: (plan: PaidPlan, interval: Interval) => void;
  methods: Method[];
  live: boolean;
  /** End of the running manual plan: a renewal of it follows on when paid before then. */
  runsUntil: { plan: string; end: string } | null;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const formId = useId();
  const summaryTitle = useId();
  const [address, setAddress] = useState<Address>(initial);
  const [method, setMethod] = useState<Method>('transfer');
  const vatId = useSettled(address.vatId?.trim() ?? '');
  const set = (key: keyof Address) => (e: { target: { value: string } }) =>
    setAddress({ ...address, [key]: e.target.value });
  const names = new Intl.DisplayNames([intlLocale(i18n.language)], { type: 'region' });
  const quote = useQuery({
    queryKey: ['billing', 'quote', `${plan}:${interval}`, address.country, vatId],
    queryFn: () =>
      unwrap(
        api.GET('/billing/quote', {
          params: {
            query: {
              planCode: plan,
              interval,
              country: address.country,
              ...(vatId ? { vatId } : {}),
            },
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });
  const checked = { ...address, vatId: address.vatId ?? '' };
  const order = useMutation({
    mutationFn: async (sent: typeof checked): Promise<{ url: string } | { invoiceId: string }> => {
      const body = { planCode: plan, interval, address: sent };
      return method === 'online'
        ? // Online: the same checked address and VAT go to Stripe, which takes the payment.
          { url: (await unwrap(api.POST('/billing/checkout-session', { body }))).url }
        : { invoiceId: (await unwrap(api.POST('/billing/invoices', { body }))).id };
    },
    onSuccess: (result) => {
      // The new order (and the address) are known when the customer comes back here.
      void client.invalidateQueries({ queryKey: ['billing'] });
      if ('url' in result) go(result.url);
      else void navigate(`/billing/invoices/${result.invoiceId}?ordered=1`);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    order.mutate(checked);
  };
  // The first field the server refused gets the focus.
  useEffect(() => {
    if (!refusedAddress(order.error)) return;
    document.getElementById(formId)?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [order.error, formId]);
  // Ordered meanwhile in another tab: the API names that order.
  const openId =
    order.error instanceof ApiError && order.error.code === 'CONFLICT'
      ? (order.error.problem as { invoiceId?: string }).invoiceId
      : undefined;
  const q = quote.data;
  const money = (cents: number) => formatMoney(cents, i18n.language);
  const price = (cents: number) => formatPrice(cents, i18n.language);
  const chosen = plans.find((p) => p.code === plan);
  // Renewing the running plan on invoice: the new term follows on if paid in time (SP-A2.78).
  const timeZone = useMe().data?.account.timezone;
  const renewal =
    runsUntil?.plan === plan ? formatDate(runsUntil.end, i18n.language, timeZone) : null;
  // The field the API refused gets the error, not always the VAT ID (SP-A2.79). The answer
  // holds for the address sent: a field changed since, even while the order was on its way,
  // no longer carries it.
  const failed = order.error instanceof ApiError ? order.error : undefined;
  const unchanged = (key: keyof Address) =>
    order.variables !== undefined && (order.variables[key] ?? '') === (address[key] ?? '');
  const refused = (key: keyof Address) =>
    failed?.code === 'VALIDATION_FAILED' &&
    failed.fieldError(`address.${key}`) !== undefined &&
    unchanged(key)
      ? t('checkout.fieldInvalid')
      : undefined;
  const vatRefused =
    failed?.code === 'VAT_ID_INVALID' && unchanged('vatId') && unchanged('country');
  const vatReason = vatRefused
    ? (failed.problem as { reason?: string }).reason === 'country'
      ? 'mismatch'
      : 'invalid'
    : q?.vatIdStatus === 'invalid' || q?.vatIdStatus === 'mismatch'
      ? q.vatIdStatus
      : undefined;
  // Every refused part of the address changed: the notice over the button goes as well.
  const answered =
    failed?.code === 'VALIDATION_FAILED'
      ? (failed.problem.errors ?? []).length > 0 &&
        (failed.problem.errors ?? []).every(
          (e) => e.path.startsWith('address.') && !unchanged(e.path.slice(8) as keyof Address),
        )
      : failed?.code === 'VAT_ID_INVALID' && !vatRefused;
  const vatError =
    vatReason === 'invalid'
      ? t('checkout.vatIdInvalid')
      : vatReason === 'mismatch'
        ? t('checkout.vatIdStatus.mismatch')
        : refused('vatId');

  return (
    <div className="co">
      <div className="acct">
        <form id={formId} onSubmit={submit}>
          <Card>
            <h2>{t('checkout.planTitle')}</h2>
            <div className="fields">
              <SelectField
                label={t('checkout.plan')}
                value={plan}
                onChange={(e) => onChoose(e.target.value as PaidPlan, interval)}
              >
                {plans.map((p) => (
                  <option key={p.code} value={p.code}>
                    {`${t(`admin.plans.${p.code}`)} · ${price(p.priceMonthCents)} / ${t('billing.per.month')}`}
                  </option>
                ))}
              </SelectField>
              <SelectField
                label={t('billing.intervalLabel')}
                value={interval}
                onChange={(e) => onChoose(plan, e.target.value as Interval)}
              >
                <option value="month">
                  {`${t('billing.intervals.month')} · ${price(chosen?.priceMonthCents ?? 0)}`}
                </option>
                <option value="year">
                  {`${t('billing.intervals.year')} · ${price(chosen?.priceYearCents ?? 0)} (${t('checkout.yearFree')})`}
                </option>
              </SelectField>
            </div>
          </Card>

          <Card>
            <h2>{t('checkout.address')}</h2>
            <div className="fields">
              <TextField
                label={t('checkout.name')}
                hint={t('checkout.nameHint')}
                required
                minLength={2}
                maxLength={200}
                autoComplete="organization"
                value={address.name}
                onChange={set('name')}
                error={refused('name')}
              />
              <TextField
                label={t('checkout.street')}
                required
                minLength={3}
                maxLength={200}
                autoComplete="street-address"
                value={address.street}
                onChange={set('street')}
                error={refused('street')}
              />
            </div>
            <div className="fields3">
              <TextField
                label={t('checkout.postalCode')}
                required
                minLength={3}
                maxLength={16}
                autoComplete="postal-code"
                value={address.postalCode}
                onChange={set('postalCode')}
                error={refused('postalCode')}
              />
              <TextField
                label={t('checkout.city')}
                required
                minLength={2}
                maxLength={100}
                autoComplete="address-level2"
                value={address.city}
                onChange={set('city')}
                error={refused('city')}
              />
              <SelectField
                label={t('checkout.country')}
                value={address.country}
                onChange={set('country')}
                error={refused('country')}
              >
                {countries.map((c) => (
                  <option key={c} value={c}>
                    {names.of(c) ?? c}
                  </option>
                ))}
              </SelectField>
            </div>
            <TextField
              label={t('checkout.vatId')}
              hint={t('checkout.vatIdHint')}
              optional
              maxLength={20}
              placeholder="DE123456789"
              value={address.vatId ?? ''}
              onChange={set('vatId')}
              note={
                !vatError && (q?.vatIdStatus === 'valid' || q?.vatIdStatus === 'unavailable') ? (
                  <span
                    role="status"
                    className={`vies ${q.vatIdStatus === 'valid' ? 'ok' : 'warn'}`}
                  >
                    {q.vatIdStatus === 'valid'
                      ? t('checkout.vatIdStatus.valid', { name: q.vatIdName ?? '' })
                      : t('checkout.vatIdStatus.unavailable')}
                  </span>
                ) : undefined
              }
              error={vatError}
            />
          </Card>

          <Card>
            <fieldset className="method">
              <legend className="h4s">{t('checkout.method')}</legend>
              <div className="pick">
                {(['transfer', 'online'] as const).map((m) => {
                  const available = methods.includes(m);
                  return (
                    <label key={m}>
                      <input
                        type="radio"
                        name="method"
                        value={m}
                        checked={method === m}
                        disabled={!available}
                        onChange={() => setMethod(m)}
                      />
                      <div>
                        <b>{t(m === 'transfer' ? 'checkout.transfer' : 'checkout.card')}</b>
                        {m === 'transfer' ? (
                          <span>
                            {renewal
                              ? t('checkout.transferRenew', { date: renewal })
                              : t('checkout.transferText')}
                          </span>
                        ) : available ? (
                          <span>{t('checkout.cardText')}</span>
                        ) : (
                          <span className="soon">{t('checkout.cardSoon')}</span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </fieldset>
            {!live ? (
              <Notice kind="warning" title={t('billing.demoTitle')}>
                {t('checkout.demo')}
              </Notice>
            ) : null}
          </Card>
        </form>
      </div>

      <aside className="card summary" aria-labelledby={summaryTitle}>
        <h2 id={summaryTitle}>{t('checkout.summary')}</h2>
        {quote.isError && !q ? (
          <LoadError message={errorMessage(quote.error, t)} onRetry={() => void quote.refetch()} />
        ) : q ? (
          <>
            <div className="line">
              <span>{`${t(`admin.plans.${plan}`)} · ${t(`billing.intervalSmall.${interval}`)}`}</span>
              <b>{money(q.netCents)}</b>
            </div>
            <div className="line">
              <span>{t('invoice.net')}</span>
              <b>{money(q.netCents)}</b>
            </div>
            <div className="line">
              <span>{t('invoice.vat', { rate: vatLabel(q.vatRateBp) })}</span>
              <b>{money(q.vatCents)}</b>
            </div>
            <div className="total">
              <span>{t('invoice.gross')}</span>
              <b>{money(q.grossCents)}</b>
            </div>
            {q.vatNote ? <p className="hint">{t(`invoice.vatNote.${q.vatNote}`)}</p> : null}
          </>
        ) : (
          <Loading />
        )}
        <p className="hint">
          {method === 'online' ? t('checkout.renewsOnline') : t('checkout.startsOnPayment')}
        </p>
        {order.isError && !answered ? (
          openId ? (
            <OpenOrder id={openId} />
          ) : (
            <Notice kind="error">{errorMessage(order.error, t)}</Notice>
          )
        ) : null}
        <Button type="submit" form={formId} size="lg" busy={order.isPending}>
          {method === 'online' ? t('checkout.submitOnline') : t('checkout.submit')}
        </Button>
        <p className="hint">{t('checkout.terms')}</p>
      </aside>
    </div>
  );
}

/** An order on invoice waits for payment: pay or cancel it first (sheet: state "open-order"). */
function OpenOrder({ id }: { id: string }) {
  const { t } = useTranslation();
  return (
    <>
      <Notice kind="warning" title={t('checkout.openTitle')}>
        {t('checkout.openText')}
      </Notice>
      <div className="acts">
        <LinkButton to={`/billing/invoices/${id}`} variant="secondary">
          {t('checkout.showOpen')}
        </LinkButton>
      </div>
    </>
  );
}

/** Order a plan on invoice or online (T-48, T-63; checkout.html). */
export function Checkout() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const planParam = params.get('plan');
  const plan: PaidPlan = isPlan(planParam) ? planParam : 'business';
  const interval: Interval = params.get('interval') === 'year' ? 'year' : 'month';
  const billing = useQuery({ queryKey: ['billing'], queryFn: () => unwrap(api.GET('/billing')) });
  const address = useQuery({
    queryKey: ['billing', 'address'],
    queryFn: () => unwrap(api.GET('/billing/address')),
  });
  const plans = useQuery({ queryKey: ['plans'], queryFn: () => unwrap(api.GET('/billing/plans')) });
  const invoices = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => unwrap(api.GET('/billing/invoices')),
  });
  const queries = [billing, address, plans, invoices];

  let content;
  if (queries.some((q) => q.isPending)) content = <Loading />;
  else if (!billing.data || !address.data || !plans.data || !invoices.data) {
    const failed = queries.find((q) => q.isError);
    content = (
      <LoadError
        message={errorMessage(failed?.error, t)}
        onRetry={() => void Promise.all(queries.map((q) => q.refetch()))}
      />
    );
  } else {
    // The API takes no second plan order on invoice while one waits for payment (T-71: orders of
    // services do not count), so the page leads to it at once.
    const open = invoices.data.items.find(
      (i) => i.status === 'open' && i.method === 'bank_transfer' && i.planCode !== null,
    );
    content = open ? (
      <div className="acct">
        <OpenOrder id={open.id} />
      </div>
    ) : (
      <OrderForm
        initial={address.data.address}
        countries={address.data.countries}
        plans={plans.data.items.filter((p) => isPlan(p.code))}
        plan={plan}
        interval={interval}
        onChoose={(p, i) => setParams({ plan: p, interval: i }, { replace: true })}
        methods={billing.data.methods}
        live={billing.data.live}
        runsUntil={runsUntil(billing.data.subscription)}
      />
    );
  }

  return (
    <div className="page">
      <div className="page-title">
        <div>
          <Link className="back" to="/billing">
            {t('billing.back')}
          </Link>
          <h1>{t('checkout.title')}</h1>
        </div>
      </div>
      {content}
    </div>
  );
}
